/**
 * Audio Analysis Prompt for Add Track Context
 * Simplified prompt for fine-tuned model
 */

export const createAddTrackAnalysisPrompt = (collectionData?: { 
  genres: { displayName: string }[], 
  tags: { displayName: string }[],
  categorizedTags?: {
    moods: string[],
    temporal: string[],
    instruments: string[],
    energy: string[],
    other: string[]
  }
}): string => {
  const collectionDataSection = collectionData ? `

### Available Collection Data:

**Genres:**
${collectionData.genres.map(g => `- ${g.displayName}`).join('\n')}

**Categorized Tags:**
${collectionData.categorizedTags ? `
**Moods:** ${collectionData.categorizedTags.moods.join(', ')}
**Temporal:** ${collectionData.categorizedTags.temporal.join(', ')}
**Instruments:** ${collectionData.categorizedTags.instruments.join(', ')}
**Energy:** ${collectionData.categorizedTags.energy.join(', ')}
**Other:** ${collectionData.categorizedTags.other.join(', ')}` : 
`**All Tags:** ${collectionData.tags.map(t => t.displayName).join(', ')}`}

### Category-Based Analysis Process:
1. **Audio Analysis** → identify all musical elements
2. **Category Scoring** → score each category systematically:
   - **Moods**: Score each mood tag (0-100) - select multiple if highly relevant
   - **Temporal**: Score temporal tags (0-100) - ONLY select if score >70 
   - **Instruments**: Score instruments (0-100) - ONLY hero/solo instruments
   - **Energy**: Score energy tags (0-100) - select multiple if relevant
   - **Other**: Score other tags (0-100) - select if highly relevant
3. **Validation Pass** → re-score with fresh ears and validate
4. **Final Selection** → use ONLY high-scoring validated items

**CRITICAL RULES:**
- Temporal tags only if scoring >70 (avoid forced "contemporary")
- Instruments only if hero/solo prominence 
- Multiple selections allowed per category if scores justify
- One genre maximum from available list` : '';

  return `FIRST: Check track duration and use correct terminology:
- Under 7 seconds: Use "mnemonic" instead of "track"
- 7 to 15 seconds: MUST use "sting" instead of "track"
- Over 15 seconds: Use "track"
${collectionDataSection}

Analyze this audio and provide a description following this pattern:
"[mood] [time period] [genre] [mnemonic/sting/track] with [instruments], [additional characteristics] atmosphere"

Focus on:
- One primary genre from the available collection
- Prominent mood and characteristics
- Only hero instruments (main/prominent instruments, not background elements)
- Descriptive atmosphere that aids in search and categorization

Provide a concise, natural description suitable for music library search.`;
};